import { create } from "zustand";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import capitalize from "lodash.capitalize";

dayjs.extend(utc);

interface DialogUploadEditPostState {
  userPost: any;
  professionals: any[];
  post: any;
  showLoader: boolean;
  textLoader: string;
  description: string;
  pageLoading: boolean;

  initializePage: (postEdit: any) => Promise<void>;
  disposePage: () => Promise<void>;
  fetchUserInitPost: () => Promise<void>;
  fetchMusicData: (soundId: string) => Promise<void>;
  onChangeText: (value: string) => Promise<void>;

  // * ---- Upload Setting ---- * //
  layout: string;
  selectedPrivacies: string[];
  button: any;
  whoCanSeeThis: string;
  selectedLinkButton: string;
  link: string;
  iosLink: string;
  androidLink: string;
  music: any;

  eventTitle: string;
  eventDate: string;
  eventStartingTime: string;
  eventDuration: number;
  eventLocation: string;

  applyJobClosingDate: string;
  applyJobProfessional: string;

  setLayout: (value: string) => Promise<void>;
  setWhoCanSeeThis: (value: string) => Promise<void>;
  toggleItemPricacy: (item: string) => Promise<void>;
  setSelectedLinkButton: (item: string) => Promise<void>;
  setLink: (value: string) => Promise<void>;
  setIosLink: (value: string) => Promise<void>;
  setAndroidLink: (value: string) => Promise<void>;
  setMusic: (music: any) => Promise<void>;

  setEventTitle: (value: string) => void;
  setEventDate: (value: string) => void;
  setEventStartingTime: (value: string) => void;
  setEventDuration: (value: string) => void;
  setEventLocation: (value: string) => void;

  setApplyJobClosingDate: (value: string) => void;
  setApplyJobProfessional: (value: string) => void;

  // * ---- Post Submit ---- * //
  onPostSubmit: (onSuccess: (post: any) => void) => Promise<void>;
}

const useDialogUploadEditPostStore = create<DialogUploadEditPostState>((set, get) => ({
  userPost: null,
  post: null,
  professionals: [],
  showLoader: false,
  textLoader: "",
  description: "",
  pageLoading: true,

  initializePage: async (postEdit: any) => {
    try {
      let privacies: string[] = [];
      if (postEdit.allow_comment === "Yes") {
        privacies.push("Allow Comment");
      }
      if (postEdit.allow_share === "Yes") {
        privacies.push("Allow Share");
      }
      if (postEdit.allow_add_to_favourite === "Yes") {
        privacies.push("Allow Add To Favourite");
      }

      set({
        post: postEdit,
        showLoader: true,
        textLoader: "Initializing...",
        layout: `${postEdit.media_template}`.toLowerCase(),
        music: null,
        selectedPrivacies: privacies,
        whoCanSeeThis: postEdit.who_can_see_this,
        selectedLinkButton: postEdit.link_button ?? "",
        link: postEdit.link_url ?? "",
        iosLink: postEdit.link_ios_url ?? "",
        androidLink: postEdit.link_android_url ?? "",

        eventTitle: postEdit.event_title ?? "",
        eventDate: postEdit.event_date ?? "",
        eventStartingTime: postEdit.event_time ?? "",
        eventDuration: Service.toInteger(postEdit.event_duration),
        eventLocation: postEdit.event_location ?? "",

        applyJobClosingDate: postEdit.closing_date ?? "",
        applyJobProfessional: postEdit.professional ?? "",
      });

      await useDialogUploadEditPostStore.getState().fetchUserInitPost();
      if (postEdit.sound_id) {
        await useDialogUploadEditPostStore.getState().fetchMusicData(`${postEdit.sound_id}`);
      }
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ showLoader: false, textLoader: "", pageLoading: false });
    }
  },

  disposePage: async () => {
    try {
      set({ pageLoading: true });
    } catch (error) {
      AppLog.show({
        message: `[disposePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchUserInitPost: async () => {
    try {
      let url = RouteApi.getUserInitPost;
      url += `token=${Service.token}`;

      const response = await RequestApi(url);
      if (response.status === "success") {
        set({ userPost: response.user, professionals: response.professionals });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchUserInitPost]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchMusicData: async (soundId: string) => {
    try {
      let url = RouteApi.getMusicData;
      url += `token=${Service.token}`;
      url += `&sound_id=${soundId}`;

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        set({ music: responses.music });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchMusicData]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onChangeText: async (value: string) => {
    try {
      set({
        description: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[onChangeText]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ---- Upload Setting ---- * //

  layout: "default",
  selectedPrivacies: ["Allow Comment", "Allow Share", "Allow Add To Favourite"],
  button: null,
  whoCanSeeThis: "everyone",
  selectedLinkButton: "None",
  link: "",
  iosLink: "",
  androidLink: "",
  music: null,

  eventTitle: "",
  eventDate: "",
  eventStartingTime: "",
  eventDuration: 0,
  eventLocation: "",

  applyJobClosingDate: "",
  applyJobProfessional: "",

  setLayout: async (value: string) => {
    try {
      set({
        layout: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setLayout]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setWhoCanSeeThis: async (value: string) => {
    try {
      set({
        whoCanSeeThis: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setWhoCanSeeThis]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  toggleItemPricacy: async (item: string) => {
    try {
      set((state) => ({
        selectedPrivacies: state.selectedPrivacies.includes(item) ? state.selectedPrivacies.filter((i) => i !== item) : [...state.selectedPrivacies, item],
      }));
    } catch (error) {
      AppLog.show({
        message: `[toggleItemPricacy]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setSelectedLinkButton: async (item: string) => {
    try {
      set({
        selectedLinkButton: item,
      });
    } catch (error) {
      AppLog.show({
        message: `[setWhoCanSeeThis]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setLink: async (value: string) => {
    try {
      set({
        link: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setIosLink: async (value: string) => {
    try {
      set({
        iosLink: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setIosLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setAndroidLink: async (value: string) => {
    try {
      set({
        androidLink: value,
      });
    } catch (error) {
      AppLog.show({
        message: `[setAndroidLink]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setMusic: async (music: any) => {
    try {
      set({ music: music });
    } catch (error) {
      AppLog.show({
        message: `[setMusic]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setEventTitle: (value: string) => set({ eventTitle: value }),
  setEventDate: (value: string) => set({ eventDate: value }),
  setEventStartingTime: (value: string) => set({ eventStartingTime: value }),
  setEventDuration: (value: string) => set({ eventDuration: Service.toInteger(value) }),
  setEventLocation: (value: string) => set({ eventLocation: value }),

  setApplyJobClosingDate: (value: string) => set({ applyJobClosingDate: value }),
  setApplyJobProfessional: (value: string) => set({ applyJobProfessional: value }),

  // * ---- Post Submit ---- * //

  onPostSubmit: async (onSuccess: (post: any) => void) => {
    try {
      set({ showLoader: true, textLoader: "Uploading ..." });
      const {
        post,
        description,
        selectedPrivacies,
        whoCanSeeThis,
        layout,
        selectedLinkButton,
        link,
        iosLink,
        androidLink,
        music,
        eventTitle,
        eventDate,
        eventStartingTime,
        eventDuration,
        eventLocation,

        applyJobClosingDate,
        applyJobProfessional,
      } = get();

      const privacyYesNo = (key: string) => (selectedPrivacies.includes(key) ? "Yes" : "No");

      let objPost = structuredClone(post);
      objPost.media_title = description;
      objPost.allow_comment = privacyYesNo("Allow Comment");
      objPost.allow_share = privacyYesNo("Allow Share");
      objPost.allow_add_to_favourite = privacyYesNo("Allow Add To Favourite");
      objPost.who_can_see_this = whoCanSeeThis;

      if (music) {
        objPost.audio_path = "";
        objPost.audio_type = "AUDIO_URL";
        objPost.sound_title = music.sound_title;
        objPost.sound_author = music.sound_author;
        objPost.sound_duration = music.sound_duration;
        objPost.sound_url = music.sound_url;
        objPost.sound_src = music.sound_src;
        objPost.sound_id = music.sound_id;
        objPost.sound_picture_url = music.sound_picture_url;
      } else {
        objPost.audio_path = "";
        objPost.audio_type = "";
        objPost.sound_title = "";
        objPost.sound_author = "";
        objPost.sound_duration = "";
        objPost.sound_url = "";
        objPost.sound_src = "";
        objPost.sound_id = "";
        objPost.sound_picture_url = "";
      }
      // Sound
      objPost.sound_starting_at = 0;
      objPost.sound_ending_at = 0;
      objPost.link_button = selectedLinkButton === "None" ? "" : selectedLinkButton;
      objPost.link_url = link;
      objPost.link_ios_url = iosLink;
      objPost.link_android_url = androidLink;

      if (post.media_type === "photo" || post.media_type === "album") {
        objPost.media_template = capitalize(layout);
      }

      if (selectedLinkButton === "Join Event") {
        objPost.event_title = eventTitle;
        objPost.event_date = eventDate;
        objPost.event_time = eventStartingTime;
        objPost.event_duration = eventDuration;
        objPost.event_location = eventLocation;

        const urlObj = new URL(eventLocation);
        const qParam = urlObj.searchParams.get("q");
        const llParam = urlObj.searchParams.get("ll");
        const coordString = qParam || llParam;

        if (coordString) {
          const [lat, lon] = coordString.split(",").map(Number);
          objPost.event_location_latitude = lat;
          objPost.event_location_longitude = lon;
        } else {
          objPost.event_location_latitude = 0;
          objPost.event_location_longitude = 0;
        }

        objPost.event_city = "";
        objPost.event_country = "";
      } else if (selectedLinkButton === "Apply Job") {
        objPost.closing_date = applyJobClosingDate;
        objPost.professional = applyJobProfessional;
      }

      let url = RouteApi.submitUpdatePost;
      url += `token=${Service.token}`;

      objPost.attachments = objPost.post_attachments;

      const posts = { posts: [objPost] };

      const response = await RequestApi(url, { data: posts });
      if (response.status === "success") {
        onSuccess(response.posts[0]);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[onPostSubmit]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ showLoader: false, textLoader: "" });
    }
  },
}));

export default useDialogUploadEditPostStore;

